/** Shared aggregates for admin & sales dashboards. */

const num = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

export function dealAmount(sub) {
    return num(sub?.data?.dealAmount ?? sub?.data?.budgetINR);
}

export function payableAmount(sub) {
    const deal = dealAmount(sub);
    return num(sub?.data?.finalPayableAmount) || deal;
}

export function statusCounts(items, statusKey = 'status') {
    return items.reduce((acc, item) => {
        const k = item[statusKey] || 'unknown';
        acc[k] = (acc[k] || 0) + 1;
        return acc;
    }, {});
}

export function aggregateClientPortfolio(subs) {
    const counts = statusCounts(subs);
    const total = subs.length;
    const approvedActive = subs.filter(
        s => s.status === 'approved' && (s.clientStatus || 'active') === 'active',
    );

    let totalDeal = 0;
    let totalPayable = 0;
    let totalCollected = 0;
    let totalBalance = 0;
    let totalGst = 0;
    let totalTds = 0;
    let totalRefunded = 0;
    let pendingPayments = 0;
    let balanceDueClients = 0;

    for (const s of approvedActive) {
        const deal = dealAmount(s);
        const payable = payableAmount(s);
        const collected = num(s.totalReceived);
        const balance = num(s.balanceAmount) || Math.max(0, payable - collected);

        totalDeal += deal;
        totalPayable += payable;
        totalCollected += collected;
        totalBalance += balance;
        totalGst += num(s.data?.gstAmount);
        totalTds += num(s.data?.tdsAmount);
        totalRefunded += num(s.totalRefunded);
        pendingPayments += (s.secondPayments || []).filter(p => p.status === 'pending').length;
        if (balance > 0) balanceDueClients += 1;
    }

    const collectionRate = totalPayable > 0
        ? Math.round((totalCollected / totalPayable) * 1000) / 10
        : 0;

    const closed = (counts.approved || 0) + (counts.rejected || 0);
    const approvalRate = closed > 0
        ? Math.round(((counts.approved || 0) / closed) * 100)
        : 0;

    return {
        counts,
        total,
        approvedActive: approvedActive.length,
        totalDeal,
        totalPayable,
        totalCollected,
        totalBalance,
        totalGst,
        totalTds,
        totalRefunded,
        collectionRate,
        approvalRate,
        pendingReview: counts.submitted || 0,
        pendingPayments,
        balanceDueClients,
    };
}

function lastPaymentDate(sub) {
    const dates = [];
    const d = sub?.data || {};
    if (d.tokenDate) {
        const t = new Date(d.tokenDate);
        if (!Number.isNaN(t.getTime())) dates.push(t);
    }
    for (const p of sub.secondPayments || []) {
        if (p.status !== 'approved') continue;
        const t = new Date(p.paymentDate || p.reviewedAt || p.createdAt);
        if (!Number.isNaN(t.getTime())) dates.push(t);
    }
    if (dates.length) return new Date(Math.max(...dates.map(x => x.getTime())));
    return new Date(sub.createdAt);
}

/** Full financial rollup for dashboard header (active approved clients). */
export function aggregateDashboardFinancials(subs, agingDays = 45) {
    const active = subs.filter(
        s => s.status === 'approved' && (s.clientStatus || 'active') === 'active',
    );

    let totalDeal = 0;
    let totalPayable = 0;
    let totalCollected = 0;
    let totalBalance = 0;
    let totalGst = 0;
    let totalTds = 0;
    let totalRefunded = 0;
    let totalToken = 0;
    let totalInstalments = 0;
    let overdueClients = 0;
    let overdueBalance = 0;
    const now = Date.now();

    for (const s of active) {
        const deal = dealAmount(s);
        const payable = payableAmount(s);
        const collected = num(s.totalReceived);
        const balance = num(s.balanceAmount) || Math.max(0, payable - collected);
        const token = num(s.effectiveTokenAmount) || num(s.data?.tokenReceivedAmount);
        const inst = (s.secondPayments || [])
            .filter(p => p.status === 'approved' && !p.promotedToToken && !p.isRefunded)
            .reduce((a, p) => a + num(p.amount), 0);

        totalDeal += deal;
        totalPayable += payable;
        totalCollected += collected;
        totalBalance += balance;
        totalGst += num(s.data?.gstAmount);
        totalTds += num(s.data?.tdsAmount);
        totalRefunded += num(s.totalRefunded);
        totalToken += token;
        totalInstalments += inst;

        if (balance > 0) {
            const days = Math.floor((now - lastPaymentDate(s).getTime()) / 86400000);
            if (days >= agingDays) {
                overdueClients += 1;
                overdueBalance += balance;
            }
        }
    }

    const collectionRate = totalPayable > 0
        ? Math.round((totalCollected / totalPayable) * 1000) / 10
        : 0;

    return {
        totalDeal,
        totalPayable,
        totalCollected,
        totalBalance,
        totalGst,
        totalTds,
        totalToken,
        totalInstalments,
        totalRefunded,
        collectionRate,
        overdueClients,
        overdueBalance,
        agingDays,
        activeClients: active.length,
    };
}

export function aggregateReimbursements(items) {
    const counts = statusCounts(items);
    const sums = items.reduce((acc, r) => {
        acc[r.status] = (acc[r.status] || 0) + num(r.amountINR);
        return acc;
    }, {});
    const total = items.length;
    const byCategory = items.reduce((m, r) => {
        const c = r.category || 'other';
        m[c] = (m[c] || 0) + num(r.amountINR);
        return m;
    }, {});
    return { counts, sums, total, byCategory };
}

export function topPerformers(subs, limit = 5) {
    const map = new Map();
    for (const s of subs) {
        if (s.status !== 'approved') continue;
        const key = s.submittedBy?._id || s.submittedBy?.name || 'unknown';
        const name = s.submittedBy?.name || 'Unknown';
        const designation = String(
            s.submittedBy?.salesDesignation
            || s.submittedBy?.designationSlug
            || s.submittedBy?.designation
            || 'other',
        ).toLowerCase();
        if (!map.has(key)) map.set(key, {
            id: key, name, designation, collected: 0, clients: 0, deal: 0,
        });
        const row = map.get(key);
        row.collected += num(s.totalReceived);
        row.deal += dealAmount(s);
        row.clients += 1;
    }
    const list = [...map.values()].sort((a, b) => b.collected - a.collected);
    const max = list[0]?.collected || 1;
    return list.slice(0, limit).map(r => ({ ...r, pct: Math.round((r.collected / max) * 100) }));
}

export function monthlyClientTrend(subs, monthCount = 6) {
    const now = new Date();
    const buckets = [];
    for (let i = monthCount - 1; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        buckets.push({
            key: `${d.getFullYear()}-${d.getMonth()}`,
            label: d.toLocaleDateString('en-IN', { month: 'short' }),
            newClients: 0,
            collected: 0,
        });
    }
    const idx = (date) => {
        const d = new Date(date);
        const key = `${d.getFullYear()}-${d.getMonth()}`;
        return buckets.findIndex(b => b.key === key);
    };
    for (const s of subs) {
        const i = idx(s.createdAt);
        if (i >= 0) buckets[i].newClients += 1;
        if (s.status === 'approved') {
            const ui = idx(s.updatedAt || s.createdAt);
            if (ui >= 0) buckets[ui].collected += num(s.totalReceived);
        }
    }
    return buckets;
}

export function buildActivityFeed({ subs = [], reimb = [], users = [], limit = 8 }) {
    const events = [];

    subs.forEach(s => {
        events.push({
            id: `sub-${s._id}`,
            at: new Date(s.createdAt),
            type: s.status === 'submitted' ? 'review' : 'client',
            urgent: s.status === 'submitted',
            title: s.status === 'submitted' ? 'New client pending review' : `Client · ${s.data?.name || 'Unnamed'}`,
            detail: s.status === 'submitted'
                ? `${s.submittedBy?.name || 'Sales'} submitted ${s.data?.name || 'a client'}`
                : `${s.submittedBy?.name || 'Sales'} · ${s.status}`,
            href: `/admin/clients/${s._id}`,
        });
        (s.secondPayments || []).filter(p => p.status === 'pending').forEach(p => {
            events.push({
                id: `pay-${p._id}`,
                at: new Date(p.createdAt || p.paymentDate || s.updatedAt),
                type: 'payment',
                urgent: true,
                title: 'New payment pending approval',
                detail: `Payment of ₹${num(p.amount).toLocaleString('en-IN')} for ${s.data?.name || 'client'}. Requires finance approval.`,
                href: '/admin/approvals',
            });
        });
    });

    reimb.filter(r => r.status === 'submitted').forEach(r => {
        events.push({
            id: `reimb-${r._id}`,
            at: new Date(r.createdAt),
            type: 'expense',
            urgent: false,
            title: 'Expense claim submitted',
            detail: `${r.user?.name || 'User'} · ₹${num(r.amountINR).toLocaleString('en-IN')} · ${r.title || 'claim'}`,
            href: '/admin/reimbursements',
        });
    });

    users.filter(u => u.status === 'pending').forEach(u => {
        events.push({
            id: `user-${u._id}`,
            at: new Date(u.createdAt),
            type: 'signup',
            urgent: true,
            title: 'New user signup',
            detail: `${u.name} (${u.email}) awaiting approval`,
            href: '/admin/approvals',
        });
    });

    return events.sort((a, b) => b.at - a.at).slice(0, limit);
}

export function buildSalesActivityFeed({ subs = [], reimb = [], limit = 8 }) {
    const events = [];
    subs.forEach(s => {
        events.push({
            id: `sub-${s._id}`,
            at: new Date(s.updatedAt || s.createdAt),
            type: s.status === 'submitted' ? 'review' : 'client',
            urgent: s.status === 'submitted',
            title: s.data?.name || 'Client',
            detail: `${s.status} · ${Math.round(num(s.receivedPercent))}% collected`,
            href: `/sales/clients/${s._id}`,
        });
        (s.secondPayments || []).filter(p => p.status === 'pending').forEach(p => {
            events.push({
                id: `pay-${p._id}`,
                at: new Date(p.createdAt || p.paymentDate || s.updatedAt),
                type: 'payment',
                urgent: true,
                title: 'Payment awaiting verification',
                detail: `₹${num(p.amount).toLocaleString('en-IN')} for ${s.data?.name || 'client'}`,
                href: `/sales/clients/${s._id}`,
            });
        });
    });
    reimb.forEach(r => {
        events.push({
            id: `reimb-${r._id}`,
            at: new Date(r.createdAt),
            type: 'expense',
            urgent: r.status === 'submitted',
            title: r.title || 'Expense claim',
            detail: `₹${num(r.amountINR).toLocaleString('en-IN')} · ${r.status}`,
            href: '/sales/reimbursements',
        });
    });
    return events.sort((a, b) => b.at - a.at).slice(0, limit);
}

export function fmtActivityTime(d) {
    const dt = new Date(d);
    const now = new Date();
    const sameDay = dt.toDateString() === now.toDateString();
    if (sameDay) {
        return dt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false });
    }
    return fmtShortDate(d);
}

const CLIENT_DATE_FIELDS = {
    birthday: ['dob', 'dateOfBirth', 'birthday', 'personalDob', 'partnerDob', 'spouseDob'],
    marriage: ['marriageDate', 'anniversaryDate', 'weddingDate'],
};

function daysUntilNext(month, day, from = new Date()) {
    const start = new Date(from.getFullYear(), from.getMonth(), from.getDate());
    let next = new Date(from.getFullYear(), month, day);
    if (next < start) next = new Date(from.getFullYear() + 1, month, day);
    return Math.ceil((next - start) / 86400000);
}

function isToday(month, day) {
    const t = new Date();
    return t.getMonth() === month && t.getDate() === day;
}

function celebrationDateLabel(month, day) {
    return new Date(2000, month, day).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

export function buildCelebrations({ users = [], subs = [], clientBasePath = '/admin/clients' }) {
    const events = [];

    for (const u of users.filter(x => x.status === 'active')) {
        const ref = u.approvedAt || u.createdAt;
        if (!ref) continue;
        const d = new Date(ref);
        if (Number.isNaN(d.getTime())) continue;
        const years = new Date().getFullYear() - d.getFullYear();
        if (years < 1) continue;
        const daysUntil = daysUntilNext(d.getMonth(), d.getDate());
        if (daysUntil > 45) continue;
        events.push({
            id: `work-${u._id}`,
            name: u.name,
            type: 'work',
            scope: 'team',
            isToday: isToday(d.getMonth(), d.getDate()),
            daysUntil,
            subtitle: `${years} year${years === 1 ? '' : 's'} with AGEON`,
            dateLabel: celebrationDateLabel(d.getMonth(), d.getDate()),
        });
    }

    for (const s of subs) {
        const data = s.data || {};
        const name = data.name || 'Client';
        for (const [type, fields] of Object.entries(CLIENT_DATE_FIELDS)) {
            for (const field of fields) {
                const raw = data[field];
                if (!raw) continue;
                const d = new Date(raw);
                if (Number.isNaN(d.getTime())) continue;
                const daysUntil = daysUntilNext(d.getMonth(), d.getDate());
                if (daysUntil > 45) continue;
                events.push({
                    id: `client-${s._id}-${field}`,
                    name,
                    type,
                    scope: 'clients',
                    isToday: isToday(d.getMonth(), d.getDate()),
                    daysUntil,
                    subtitle: type === 'birthday' ? 'Birthday' : 'Anniversary',
                    dateLabel: celebrationDateLabel(d.getMonth(), d.getDate()),
                    href: `${clientBasePath}/${s._id}`,
                });
            }
        }
    }

    return events.sort((a, b) => a.daysUntil - b.daysUntil);
}

export function fmtShortDate(d) {
    return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function fmtRelativeTime(d) {
    const diff = Date.now() - new Date(d).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 48) return `${hrs}h ago`;
    return fmtShortDate(d);
}

export function fmtUpdatedLabel() {
    return `Updated ${new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}`;
}

export function filterSubmissions(subs, { period = 'all', status = 'all' } = {}) {
    let filtered = [...subs];
    if (status !== 'all') {
        filtered = filtered.filter(s => s.status === status);
    }
    if (period !== 'all') {
        const months = period === '6m' ? 6 : 12;
        const cutoff = new Date();
        cutoff.setMonth(cutoff.getMonth() - months);
        filtered = filtered.filter(s => new Date(s.createdAt) >= cutoff);
    }
    return filtered;
}

export function expenseStatusSegments(reimbStats) {
    const { counts = {} } = reimbStats;
    return [
        { label: 'Submitted', value: counts.submitted || 0, color: '#FFCA05' },
        { label: 'Approved', value: counts.approved || 0, color: '#1a9a88' },
        { label: 'Paid', value: counts.paid || 0, color: '#0d5249' },
        { label: 'Rejected', value: counts.rejected || 0, color: '#C3161C' },
    ];
}

export function financialSummaryList(portfolio) {
    return [
        { label: 'Total deal value', value: portfolio.totalDeal, color: '#0a3530' },
        { label: 'Total payable', value: portfolio.totalPayable, color: '#0d5249' },
        { label: 'Total collected', value: portfolio.totalCollected, color: '#117a6d' },
        { label: 'Balance due', value: portfolio.totalBalance, color: '#C3161C' },
        { label: 'GST', value: portfolio.totalGst, color: '#1a9a88' },
        { label: 'TDS', value: portfolio.totalTds, color: '#3dbfb0' },
    ];
}
